import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Tag, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  message,
  Tooltip,
  Modal
} from 'antd';
import { 
  HistoryOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  UserOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext.jsx';
import apiService from '../services/apiService';
import moment from 'moment';

const { Option } = Select;
const { RangePicker } = DatePicker;

const AppActionsHistory = ({ sessionId, projectId, visible, onClose }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    sessionId: sessionId,
    projectId: projectId,
    action: null,
    appId: null,
    limit: 50,
    offset: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  const { getAuthToken } = useAuth();

  useEffect(() => {
    if (visible) {
      loadActionsHistory();
    }
  }, [visible, filters]);

  const loadActionsHistory = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      const response = await apiService.getAppActionsHistory(token, filters);
      
      setActions(response.actions || []);
      setPagination(prev => ({
        ...prev,
        total: response.count || 0
      }));
    } catch (error) {
      console.error('Failed to load actions history:', error);
      message.error('Failed to load actions history');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0 // Reset to first page when filtering
    }));
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleTableChange = (paginationInfo) => {
    const offset = (paginationInfo.current - 1) * paginationInfo.pageSize;
    setFilters(prev => ({
      ...prev,
      offset: offset,
      limit: paginationInfo.pageSize
    }));
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }));
  };

  const getActionIcon = (action) => {
    switch(action?.toLowerCase()) {
      case 'approved':
        return <CheckCircleOutlined style={{ color: '#22c55e' }} />;
      case 'declined':
        return <CloseCircleOutlined style={{ color: '#ef4444' }} />;
      default:
        return <AppstoreOutlined />;
    }
  };

  const getActionColor = (action) => {
    switch(action?.toLowerCase()) {
      case 'approved':
        return 'green';
      case 'declined':
        return 'red';
      default:
        return 'blue';
    }
  };

  const columns = [
    {
      title: 'App ID',
      dataIndex: 'app_id',
      key: 'app_id',
      width: 120,
      render: (appId) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {appId}
        </span>
      )
    },
    {
      title: 'App Name',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 180,
      render: (name) => (
        <Tooltip title={name}>
          <span style={{ 
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {name || 'Unknown App'}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {getActionIcon(action)}
          <Tag color={getActionColor(action)}>
            {action?.toUpperCase() || 'UNKNOWN'}
          </Tag>
        </div>
      )
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => (
        <Tooltip title={reason}>
          <span style={{ 
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px'
          }}>
            {reason || 'No reason provided'}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 80,
      render: (userId) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <UserOutlined style={{ fontSize: '12px' }} />
          <span style={{ fontSize: '12px' }}>{userId}</span>
        </div>
      )
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 140,
      render: (timestamp) => (
        <span style={{ fontSize: '12px' }}>
          {moment(timestamp).format('MMM DD, HH:mm')}
        </span>
      )
    },
    {
      title: 'Session',
      dataIndex: 'session_id',
      key: 'session_id',
      width: 100,
      render: (sessionId) => (
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '10px',
          color: '#666'
        }}>
          ...{sessionId?.slice(-8)}
        </span>
      )
    }
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <HistoryOutlined />
          <span>App Actions History</span>
          <Tag color="blue">{pagination.total} actions</Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Filter by action"
            style={{ width: 140 }}
            allowClear
            value={filters.action}
            onChange={(value) => handleFilterChange('action', value)}
          >
            <Option value="approved">Approved</Option>
            <Option value="declined">Declined</Option>
          </Select>
          
          <Input
            placeholder="Search by App ID"
            prefix={<SearchOutlined />}
            style={{ width: 160 }}
            value={filters.appId}
            onChange={(e) => handleFilterChange('appId', e.target.value || null)}
          />
          
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setFilters({
                sessionId: sessionId,
                projectId: projectId,
                action: null,
                appId: null,
                limit: 50,
                offset: 0
              });
            }}
          >
            Clear Filters
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={actions}
        rowKey={(record) => `${record.app_id}-${record.timestamp}`}
        loading={loading}
        size="small"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} actions`,
        }}
        onChange={handleTableChange}
      />
    </Modal>
  );
};

export default AppActionsHistory;